import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Data } from '@angular/router';

/** SEO route data shape expected in route `data.seo`. */
export interface SeoRouteData {
  index: boolean;
  canonicalPath?: string;
  title?: string;
  description?: string;
}

type RouteDataWithSeo = Data & { seo?: SeoRouteData };

const DEFAULT_TITLE = 'Kirigami';
const DEFAULT_DESCRIPTION = 'Kirigami: AI-powered document analysis platform.';

/**
 * SeoService — browser-only SEO metadata management.
 *
 * Responsibilities:
 * - Set document `<title>` via Angular Title service
 * - Set `<meta name="description">` via Angular Meta service
 * - Set/remove `<link rel="canonical">`
 * - Set Open Graph tags (og:title, og:description, og:url)
 * - Provide `updateFromRouteData(data)` to apply SEO metadata from route data
 *
 * Evidence: docs/discovery/05-platform-boundaries.md
 * Source: kirigami-analysis/src/app/shared/services/seo-meta.service.ts
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  /** Set the document `<title>`. */
  setTitle(title: string): void {
    this.titleService.setTitle(title);
    this.meta.updateTag({ property: 'og:title', content: title });
  }

  /** Set `<meta name="description">` and `og:description`. */
  setDescription(description: string): void {
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:description', content: description });
  }

  /** Set or remove `<link rel="canonical">` and `og:url`. */
  setCanonical(href: string | null): void {
    if (href) {
      this.upsertCanonicalLink(href);
      this.meta.updateTag({ property: 'og:url', content: href });
    } else {
      this.removeCanonicalLink();
      this.meta.removeTag("property='og:url'");
    }
  }

  /**
   * Read SEO metadata from route data and apply title, description,
   * canonical URL, and robots directives.
   *
   * @param data - merged route data object; expects an optional `seo` key
   * @param currentUrl - the current router URL (used to build canonical when none specified)
   */
  updateFromRouteData(data: RouteDataWithSeo, currentUrl?: string): void {
    const seo: SeoRouteData = data?.seo ?? { index: true };

    // Title
    const title = seo.title ?? DEFAULT_TITLE;
    this.setTitle(title);

    // Description
    const description = seo.description ?? DEFAULT_DESCRIPTION;
    this.setDescription(description);

    // Robots
    this.meta.updateTag({
      name: 'robots',
      content: seo.index ? 'index, follow' : 'noindex, nofollow',
    });

    // Canonical URL
    const canonicalUrl = this.buildCanonicalUrl(seo.canonicalPath, currentUrl);
    this.setCanonical(canonicalUrl);
  }

  // --- private helpers ---

  private buildCanonicalUrl(canonicalPath?: string, currentUrl?: string): string {
    const currentPath = (currentUrl ?? '/').split('?')[0]?.split('#')[0] || '/';
    const rawPath = canonicalPath ?? currentPath;
    const normalizedPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    const origin = this.document.location?.origin ?? '';

    if (!origin) {
      return normalizedPath || '/';
    }

    return normalizedPath === '/' ? `${origin}/` : `${origin}${normalizedPath}`;
  }

  private upsertCanonicalLink(href: string): void {
    let link = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private removeCanonicalLink(): void {
    const link = this.document.querySelector("link[rel='canonical']");
    if (link) {
      link.remove();
    }
  }
}
